/**
 * Retail POS catalogue cache: a slim snapshot in IndexedDB for instant search,
 * refreshed in the background so the till keeps working through network blips.
 */
import { api } from "@/store/useStore";

const DB_NAME = "hostpoint_pos";
const STORE = "catalog";

const openDb = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const idbGet = async (key) => {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch (e) {
    return undefined;
  }
};

const idbSet = async (key, value) => {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (e) {
    /* cache is best-effort */
  }
};

const cacheKey = (outletId) => `catalog:${outletId || "default"}`;

export const readCachedCatalog = (outletId) => idbGet(cacheKey(outletId));

export const fetchCatalog = async (outletId) => {
  const params = outletId ? `?outlet_id=${encodeURIComponent(outletId)}` : "";
  const data = await api.get(`/pos/catalog${params}`);
  const snapshot = { ...data, cached_at: new Date().toISOString() };
  await idbSet(cacheKey(outletId), snapshot);
  return snapshot;
};

export const serverSearch = async (q, outletId) => {
  const params = new URLSearchParams({ q });
  if (outletId) params.append("outlet_id", outletId);
  const data = await api.get(`/pos/search?${params.toString()}`);
  return data.products || [];
};

const norm = (value) => String(value || "").toLowerCase();

/** Exact scan hit: returns { product, variant } or null. */
export const findByCode = (products, code) => {
  const needle = String(code || "").trim();
  if (!needle) return null;
  const lower = needle.toLowerCase();
  for (const product of products) {
    for (const variant of product.variants || []) {
      if (variant.barcode === needle || norm(variant.sku) === lower) {
        return { product, variant };
      }
    }
  }
  for (const product of products) {
    if (product.barcode === needle || norm(product.sku) === lower) {
      return { product, variant: null };
    }
  }
  return null;
};

/** Ranked local search across name, brand, SKU, barcode and variant labels. */
export const searchCatalog = (products, query, { categoryId, groupId, limit = 60 } = {}) => {
  const terms = norm(query).split(/\s+/).filter(Boolean);
  const scored = [];

  for (const product of products) {
    if (categoryId && categoryId !== "all" && product.category_id !== categoryId) continue;
    if (groupId && groupId !== "all" && product.group_id !== groupId) continue;

    if (terms.length === 0) {
      scored.push([0, product]);
      continue;
    }

    const name = norm(product.name);
    const brand = norm(product.brand);
    const sku = norm(product.sku);
    const variantText = norm((product.variants || []).map((v) => `${v.label} ${v.sku} ${v.barcode}`).join(" "));
    let score = 0;
    let matchedAll = true;

    for (const term of terms) {
      let termScore = 0;
      if (name.startsWith(term)) termScore += 12;
      else if (name.includes(term)) termScore += 8;
      if (brand.includes(term)) termScore += 4;
      if (sku.includes(term)) termScore += 5;
      if (String(product.barcode || "").includes(term)) termScore += 6;
      if (variantText.includes(term)) termScore += 3;
      if (termScore === 0) { matchedAll = false; break; }
      score += termScore;
    }
    if (matchedAll) scored.push([score, product]);
  }

  scored.sort((a, b) => b[0] - a[0] || a[1].name.localeCompare(b[1].name));
  return { results: scored.slice(0, limit).map((pair) => pair[1]), total: scored.length };
};
